<script setup lang="ts">
import ClientDetailsSheet from '~/components/clients/ClientDetailsSheet.vue';
import ClientContactDialog from '~/components/clients/ClientContactDialog.vue';
import TaskDetailsSheet from '~/components/tasks/TaskDetailsSheet.vue';
import ApprovalResponseDialog from '~/components/projects/ApprovalResponseDialog.vue';
import ClientOnboardingHandoffDialog, { type ClientOnboardingHandoffPayload } from '@/components/clients/ClientOnboardingHandoffDialog.vue';
import type { Approval, Client, ClientContact, Project } from '~/types/models';

interface Props {
    linkedClient: Client | null;
    relatedProjects: Project[];
    editingContact: ClientContact | null;
    selectedTaskId: string | null;
    selectedApproval: Approval | null;
    approvalSaving: boolean;
    startingOnboarding: boolean;
    contactDeleting: boolean;
    clientDeleting: boolean;
    canDeleteClient: boolean;
    handleSaveContact: (contact: ClientContact) => void | Promise<void>;
    handleDeleteContact: (contact: ClientContact) => void | Promise<void>;
    handleAddContact: () => void;
    handleEditContact: (contact: ClientContact) => void;
    handleEditLinkedClient: (value: unknown) => void | Promise<void>;
    handleDeleteLinkedClient: () => void | Promise<void>;
    handleTaskUpdated: () => void | Promise<void>;
    handleTaskDeleted: () => void | Promise<void>;
    handleSaveApprovalResponse: (payload: { approval: Approval; status: 'pending' | 'approved' | 'rejected'; note: string; message: string }) => void | Promise<void>;
    handleStartOnboarding: (payload: ClientOnboardingHandoffPayload) => void | Promise<void>;
}

defineProps<Props>();

const isContactDialogOpen = defineModel<boolean>('contactOpen', { default: false });
const isEditClientDialogOpen = defineModel<boolean>('editClientOpen', { default: false });
const isTaskDetailOpen = defineModel<boolean>('taskOpen', { default: false });
const isApprovalDialogOpen = defineModel<boolean>('approvalOpen', { default: false });
const isOnboardingHandoffOpen = defineModel<boolean>('onboardingOpen', { default: false });
</script>

<template>
    <ClientContactDialog
        :open="isContactDialogOpen"
        :contact="editingContact"
        :projects="relatedProjects"
        :can-delete="canDeleteClient"
        :deleting="contactDeleting"
        @update:open="(value) => { isContactDialogOpen = value; }"
        @save="handleSaveContact"
        @delete="handleDeleteContact"
    />

    <ClientDetailsSheet
        v-model:open="isEditClientDialogOpen"
        :client="linkedClient"
        :save="handleEditLinkedClient"
        :can-delete="canDeleteClient"
        :deleting="clientDeleting"
        @add-contact="handleAddContact"
        @edit-contact="handleEditContact"
        @delete="handleDeleteLinkedClient"
    />

    <TaskDetailsSheet
        v-model:open="isTaskDetailOpen"
        :task-id="selectedTaskId"
        :project-options="relatedProjects.map((project) => ({
            id: project.id,
            name: project.name,
            clientId: project.clientId,
            stages: project.stages,
        }))"
        :client-options="linkedClient ? [{ id: linkedClient.id, name: linkedClient.name }] : []"
        @task-updated="handleTaskUpdated"
        @task-deleted="handleTaskDeleted"
    />

    <ApprovalResponseDialog
        v-model:open="isApprovalDialogOpen"
        :approval="selectedApproval"
        :saving="approvalSaving"
        @save="handleSaveApprovalResponse"
    />

    <ClientOnboardingHandoffDialog
        v-if="linkedClient"
        v-model:open="isOnboardingHandoffOpen"
        :client="linkedClient"
        :projects="relatedProjects"
        :submitting="startingOnboarding"
        @start="handleStartOnboarding"
    />
</template>
